import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Camera, MapPin, LogIn, Loader2, ArrowLeft, AlertCircle } from "lucide-react";

export default function PromoterCheckIn() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const storeParam = params.get("store");

  const [coords, setCoords] = useState(null);
  const [locErr, setLocErr] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api
      .get("/promoter/me")
      .then((r) => setMe(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    if (!navigator.geolocation) {
      setLocErr("Geolocation not supported on this device");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy,
        }),
      (err) => setLocErr(err.message),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }, []);

  const targetStore =
    me?.stores?.find((s) => s.id === storeParam) ||
    (me?.stores?.length === 1 ? me.stores[0] : null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!agreed) return toast.error("Please accept the acknowledgement first");
    if (!targetStore) return toast.error("Pick a store from the home page");
    if (!coords) return toast.error("Location not available");
    if (!photo) return toast.error("Please capture a photo");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("latitude", String(coords.lat));
      fd.append("longitude", String(coords.lng));
      fd.append("store_id", targetStore.id);
      fd.append("photo", photo);
      fd.append("acknowledged", "true");
      await api.post("/promoter/shift/check-in", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Checked in!");
      nav("/promoter");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {!agreed && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 backdrop-blur-sm p-4"
          data-testid="acknowledgement-overlay"
        >
          <div className="glass-panel rounded-3xl p-6 max-w-md w-full max-h-[85vh] overflow-y-auto bg-white/90">
            <div className="font-display font-bold text-xl tracking-tight">
              Promoter Acknowledgement
            </div>
            <p className="text-sm text-slate-600 mt-3">By checking in, I confirm that I:</p>
            <ul className="mt-3 space-y-2.5 text-sm text-slate-700 list-disc pl-5">
              <li>
                Will not approach anyone under 18, pregnant, or anyone who does not currently
                smoke/use nicotine products.
              </li>
              <li>Will follow only the approved ÜBBS communication and training guidelines.</li>
              <li>Will not make health, medical, safety or smoking-cessation claims.</li>
              <li>Will not provide false or unapproved product information.</li>
              <li>Will complete the required consumer surveys accurately.</li>
            </ul>
            <p className="text-sm font-medium text-slate-800 mt-4">
              I confirm that I have read, understood and agree to follow the above guidelines
              during my shift.
            </p>
            <Button
              onClick={() => setAgreed(true)}
              className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-700 text-white font-semibold mt-5"
              data-testid="acknowledgement-agree-button"
            >
              I have read &amp; understood — I agree
            </Button>
          </div>
        </div>
      )}
      <button
        onClick={() => nav(-1)}
        className="flex items-center gap-2 text-sm text-neutral-500 hover:text-black"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          Step 01
        </div>
        <h1 className="font-display font-semibold text-3xl tracking-tight">Check in</h1>
        <p className="text-sm text-neutral-500 mt-1">
          We verify you are within the store's geofence before starting your shift.
        </p>
      </div>

      <div className="glass-panel p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
          Store
        </div>
        {targetStore ? (
          <div data-testid="checkin-target-store">
            <div className="font-display font-bold text-lg">{targetStore.name}</div>
            <div className="text-sm text-neutral-500">{targetStore.address}</div>
            <div className="font-mono text-xs text-neutral-500 mt-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> radius {targetStore.radius_m}m
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-amber-700 text-sm">
            <AlertCircle className="h-4 w-4" /> No store selected. Return home and pick one.
          </div>
        )}
      </div>

      <div className="glass-panel p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
          Location
        </div>
        {coords ? (
          <div className="flex items-center gap-2 font-mono text-sm" data-testid="checkin-coords">
            <MapPin className="h-4 w-4 text-emerald-600" />
            {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
            <span className="text-xs text-neutral-500 ml-1">±{Math.round(coords.acc)}m</span>
          </div>
        ) : locErr ? (
          <div className="text-sm text-red-600">{locErr}</div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Acquiring GPS…
          </div>
        )}
      </div>

      <div className="glass-panel p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
          Selfie / Store Photo
        </div>
        {preview ? (
          <div className="space-y-3">
            <img
              src={preview}
              alt="preview"
              className="w-full aspect-square object-cover glass-panel"
            />
            <label className="block">
              <input
                type="file"
                accept="image/*"
                capture="user"
                onChange={onFile}
                className="hidden"
              />
              <Button variant="outline" className="rounded-xl w-full" asChild>
                <span>Retake</span>
              </Button>
            </label>
          </div>
        ) : (
          <label className="block" data-testid="checkin-photo-label">
            <input
              type="file"
              accept="image/*"
              capture="user"
              onChange={onFile}
              className="hidden"
              data-testid="checkin-photo-input"
            />
            <div className="border-2 border-dashed border-slate-300/70 aspect-square flex flex-col items-center justify-center cursor-pointer hover:bg-white/50">
              <Camera className="h-10 w-10 text-neutral-400" />
              <div className="mt-2 text-sm text-neutral-500">Tap to capture</div>
            </div>
          </label>
        )}
      </div>

      <Button
        disabled={busy || !coords || !photo || !targetStore}
        onClick={submit}
        className="rounded-xl w-full h-14 bg-slate-900 hover:bg-slate-700 text-white font-semibold uppercase tracking-wider"
        data-testid="confirm-check-in"
      >
        {busy ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <LogIn className="h-5 w-5 mr-2" /> Confirm check-in
          </>
        )}
      </Button>
    </div>
  );
}
